import { Component, OnInit, OnDestroy, signal, computed, inject, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, Observable } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { ComponentCanDeactivate } from '../../../core/guards/unsaved-changes.guard';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { BadgeModule } from 'primeng/badge';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { MenuItem, ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmationDialogService } from '../../../core/services/confirmation-dialog.service';
import { HtmlSanitizerService } from '../../../core/services/html-sanitizer.service';
import { SectionRegistryService } from '../../services/section-registry.service';
import { Section } from '../../models/section.model';
import { TemplateRegistryService } from '../../services/template-registry.service';
import { Template, TemplateSectionResponse } from '../../models/template.model';
import { AttachmentRegistryService } from '../../services/attachment-registry.service';
import { Attachment } from '../../models/attachment.model';
import { NavigationContextService } from '../../../core/services/navigation-context.service';
import html2pdf from 'html2pdf.js';

declare const Jodit: any;
declare const htmlDocx: any;

interface ContractSection {
  id: number;
  title: string;
  content: string;
  variables?: { id: number; displayName: string; key: string }[];
  attachments?: { id: number; title: string; description?: string }[];
}

@Component({
  selector: 'app-contract-templates',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    DividerModule,
    AccordionModule,
    TooltipModule,
    BreadcrumbModule,
    ConfirmDialogModule,
    ToastModule,
    SelectModule,
    BadgeModule
  ],
  providers: [
    ConfirmationService,
    ConfirmationDialogService,
    MessageService
  ],
  templateUrl: './contract-templates.html',
  styleUrl: './contract-templates.scss',
})
export class ContractTemplatesComponent implements OnInit, OnDestroy, ComponentCanDeactivate, AfterViewInit {
  @ViewChild('joditEditor', { static: false }) joditEditorElement!: ElementRef;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private titleService = inject(Title);
  private htmlSanitizer = inject(HtmlSanitizerService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private confirmDialogService = inject(ConfirmationDialogService);
  private sectionRegistryService = inject(SectionRegistryService);
  private templateRegistryService = inject(TemplateRegistryService);
  private attachmentRegistryService = inject(AttachmentRegistryService);
  private navigationContext = inject(NavigationContextService);
  
  isGenerating = signal<boolean>(false);
  breadcrumbItems = signal<MenuItem[]>([]);
  
  variables = [
    { displayName: 'First Name', key: 'first_name' },
    { displayName: 'Last Name', key: 'last_name' },
    { displayName: 'Full Name', key: 'full_name' },
    { displayName: 'Email', key: 'email' },
    { displayName: 'Email Secondary', key: 'email_secondary' },
    { displayName: 'Phone Number', key: 'phone_number' },
    { displayName: 'Mobile Number', key: 'mobile_number' },
    { displayName: 'Address', key: 'address' },
    { displayName: 'City', key: 'city' },
    { displayName: 'Country', key: 'country' },
    { displayName: 'Postal Code', key: 'postal_code' },
    { displayName: 'Policy Number', key: 'policy_number' },
    { displayName: 'Policy Start Date', key: 'policy_start_date' },
    { displayName: 'Policy End Date', key: 'policy_end_date' },
    { displayName: 'Premium Amount', key: 'premium_amount' },
    { displayName: 'Company Name', key: 'company_name' },
    { displayName: 'Company Address', key: 'company_address' },
    { displayName: 'Tax ID', key: 'tax_id' },
    { displayName: 'VAT Number', key: 'vat_number' },
    { displayName: 'Contract Date', key: 'contract_date' },
    { displayName: 'Contract Number', key: 'contract_number' },
  ];

  // Use Signals for state management
  sections = signal<ContractSection[]>([]);
  filteredSections = signal<ContractSection[]>([]);
  selectedSections = signal<ContractSection[]>([]);
  editVisible = signal(false);
  editContent = signal('');
  editingSectionIndex = signal<number | null>(null);
  previewVisible = signal(false);
  previewContent = signal('');
  
  // Attachments
  availableAttachments = signal<Attachment[]>([]);
  optionalAttachmentIds = signal<number[]>([]);
  addAttachmentDialogVisible = signal(false);
  attachmentSearchQuery: string = '';
  
  // Computed: attachments from sections (read-only)
  sectionAttachments = computed(() => {
    const attachmentsMap = new Map<number, Attachment>();
    this.selectedSections().forEach(section => {
      section.attachments?.forEach(att => {
        if (att.id && !attachmentsMap.has(att.id)) {
          attachmentsMap.set(att.id, att as Attachment);
        }
      });
    });
    return Array.from(attachmentsMap.values());
  });
  
  // Computed: optional attachments (template-level, editable)
  optionalAttachments = computed(() => {
    return this.optionalAttachmentIds()
      .map(id => this.availableAttachments().find(a => a.id === id))
      .filter(a => a !== undefined) as Attachment[];
  });
  
  templateName: string = '';
  templateDescription: string = '';
  searchQuery: string = '';
  templateId: number | null = null;
  domainType: string = '';
  subDomainType: string = '';
  isDomainTypeDisabled = signal<boolean>(true); // Disabilitato perché precompilato dal contesto
  
  domainTypeOptions = [
    { label: 'Fornitori', value: 'SUPPLIERS' },
    { label: 'Fiduciari', value: 'PROFESSIONALS' }
  ];
  
  subDomainTypeOptions = [
    { label: 'Contratto', value: 'CONTRACTS' },
    { label: 'Atto di Nomina', value: 'ATTO_DI_NOMINA' }
  ];
  
  isFromRegistry: boolean = false;
  isCloneMode: boolean = false;
  private originalTemplateName: string = '';
  private originalSelectedSections: ContractSection[] = [];
  private hasUnsavedTemplateChanges: boolean = false;
  private isLoadingTemplate: boolean = false;
  
  private editorInstance: any = null;
  private previewEditorInstance: any = null;
  private draggedSection: ContractSection | null = null;
  private draggedSelectedIndex: number | null = null;
  private dropTargetIndex: number | null = null;
  private variablesMap: Map<number, string> = new Map(); // Maps variable ID to displayName

  private get templatesRegistryRoute(): string {
    return `/axa/${this.navigationContext.getDomainPrefix()}-templates-registry`;
  }

  private get sectionsRegistryRoute(): string {
    return `/axa/${this.navigationContext.getDomainPrefix()}-contract-sections`;
  }

  ngOnInit() {
    this.titleService.setTitle('Gestione Template Contratti - PalEasy');
    // Precompila domainType in base al contesto di navigazione (Fornitori/Fiduciari)
    this.domainType = this.getInitialDomainType();
    
    // Inizializza breadcrumb base
    this.updateBreadcrumb();
    
    this.loadAvailableSections();
    this.loadAvailableAttachments();
    
    // Controlla se arriva da una clonazione tramite navigation state
    const navigation = this.router.getCurrentNavigation();
    const cloneData = navigation?.extras?.state?.['cloneData'] || history.state?.cloneData;
    
    if (cloneData) {
      // Modalità clone: imposta nome con prefisso "Copia di" e carica tutti i dati del template
      this.isCloneMode = true;
      this.templateName = `Copia di ${cloneData.originalName}`;
      this.templateDescription = cloneData.description || '';
      this.domainType = cloneData.domainType || 'SUPPLIERS';
      this.subDomainType = cloneData.subDomainType || 'CONTRACTS';
      
      // Converti sections dal formato API al formato ContractSection se necessario
      if (cloneData.sections && Array.isArray(cloneData.sections)) {
        const convertedSections = cloneData.sections.map((section: any) => {
          // Se è già nel formato ContractSection (con sectionId, sectionName)
          if (section.sectionId || section.sectionName) {
            // Map variables ID to displayName
            if (section.variables) {
              section.variables.forEach((v: any) => {
                this.variablesMap.set(v.id, v.displayName);
              });
            }
            return {
              id: section.sectionId || section.id,
              title: section.sectionName || section.title || '',
              content: section.contentText || section.content || '',
              variables: section.variables,
              attachments: section.attachments
            };
          }
          // Altrimenti potrebbe essere già nel formato ContractSection
          return section;
        });
        this.selectedSections.set(convertedSections);
        
        // Enrich availableAttachments with attachments from cloned sections
        convertedSections.forEach((section: any) => {
          if (section.attachments && section.attachments.length > 0) {
            section.attachments.forEach((att: any) => {
              if (att.id && !this.availableAttachments().find(a => a.id === att.id)) {
                this.availableAttachments.update(arr => [...arr, att]);
              }
            });
          }
        });
      } else {
        this.selectedSections.set([]);
      }
      
      // Load optional attachments from cloneData
      if (cloneData.optionalAttachments && Array.isArray(cloneData.optionalAttachments)) {
        const optionalIds = cloneData.optionalAttachments.map((att: any) => att.id).filter((id: any) => id !== undefined);
        this.optionalAttachmentIds.set(optionalIds);
        
        // Enrich availableAttachments with optionalAttachments from cloned template
        cloneData.optionalAttachments.forEach((att: any) => {
          if (att.id && !this.availableAttachments().find(a => a.id === att.id)) {
            this.availableAttachments.update(arr => [...arr, att]);
          }
        });
      } else if (cloneData.optionalAttachmentIds && Array.isArray(cloneData.optionalAttachmentIds)) {
        this.optionalAttachmentIds.set(cloneData.optionalAttachmentIds);
      }
      
      this.isFromRegistry = false;
      
      this.messageService.add({
        severity: 'success',
        summary: 'Template Clonato',
        detail: `Template "${cloneData.originalName}" clonato. Modifica i dati e salva.`,
        life: 4000
      });
      
      this.updateBreadcrumb();
      return;
    }
    
    // Carica template se ID presente in query params
    this.route.queryParams.subscribe(params => {
      const templateId = params['id'];
      const isClone = params['clone'] === 'true';
      this.isFromRegistry = !!templateId; // Se c'è un ID, arriva dal registro
      
      if (templateId) {
        this.loadTemplateById(templateId, isClone);
      }
    });
  }
  
  updateBreadcrumb() {
    const items: MenuItem[] = [{ 
      label: 'Gestione Template',
      routerLink: this.templatesRegistryRoute
    }];
    
    if (this.templateName) {
      items.push({ label: this.templateName });
    }
    
    this.breadcrumbItems.set(items);
  }

  sanitizeContent(content: string): SafeHtml {
    // DOMPurify (htmlSanitizer) ha già rimosso tutto il codice malevolo.
    // bypassSecurityTrustHtml evita che Angular risanitizzi l'output
    // producendo falsi warning e rimuovendo attributi personalizzati leciti.
    return this.sanitizer.bypassSecurityTrustHtml(this.htmlSanitizer.sanitize(content));
  }

  sanitizeAndFormatContent(content: string): SafeHtml {
    const formattedContent = this.formatVariablesForPreview(content);
    return this.sanitizer.bypassSecurityTrustHtml(this.htmlSanitizer.sanitize(formattedContent));
  }

  onDragStartSection(event: DragEvent, section: ContractSection) {
    this.draggedSection = section;
    const target = event.target as HTMLElement;
    target.classList.add('dragging');
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('text/plain', section.title);
    }
  }

  onDragEndSection(event: DragEvent) {
    const target = event.target as HTMLElement;
    target.classList.remove('dragging');
    this.draggedSection = null;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    // Only handle drag from available sections, not from selected sections reordering
    if (this.draggedSelectedIndex === null && event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    // Only handle adding new sections, not reordering
    if (this.draggedSection && this.draggedSelectedIndex === null) {
      this.addSection(this.draggedSection);
    }
    this.draggedSection = null;
  }

  // Drag and drop for reordering selected sections
  onDragStartSelected(event: DragEvent, section: ContractSection, index: number) {
    this.draggedSelectedIndex = index;
    const target = event.target as HTMLElement;
    target.classList.add('dragging');
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', section.title);
    }
  }

  onDragEndSelected(event: DragEvent) {
    const target = event.target as HTMLElement;
    target.classList.remove('dragging');
    this.draggedSelectedIndex = null;
    this.dropTargetIndex = null;
    // Remove all drop indicators
    document.querySelectorAll('.drop-indicator').forEach(el => el.classList.remove('drop-indicator'));
  }

  onDragOverSelected(event: DragEvent, index: number) {
    event.preventDefault();
    if (this.draggedSelectedIndex !== null && this.draggedSelectedIndex !== index) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
      this.dropTargetIndex = index;
      const target = event.currentTarget as HTMLElement;
      target.classList.add('drop-indicator');
    }
  }

  onDragLeaveSelected(event: DragEvent) {
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('drop-indicator');
  }

  onDropSelected(event: DragEvent, dropIndex: number) {
    event.preventDefault();
    event.stopPropagation();
    
    // Case 1: Adding a new section from available sections
    if (this.draggedSection && this.draggedSelectedIndex === null) {
      this.addSection(this.draggedSection);
      this.draggedSection = null;
    }
    // Case 2: Reordering existing selected sections
    else if (this.draggedSelectedIndex !== null && this.draggedSelectedIndex !== dropIndex) {
      const sections = [...this.selectedSections()];
      const draggedItem = sections[this.draggedSelectedIndex];
      
      // Remove from old position
      sections.splice(this.draggedSelectedIndex, 1);
      
      // Insert at new position
      const newIndex = this.draggedSelectedIndex < dropIndex ? dropIndex - 1 : dropIndex;
      sections.splice(newIndex, 0, draggedItem);
      
      this.selectedSections.set(sections);
    }
    
    // Cleanup
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('drop-indicator');
    this.draggedSelectedIndex = null;
    this.dropTargetIndex = null;
  }
  
  ngAfterViewInit() {
    if (this.joditEditorElement && typeof Jodit !== 'undefined') {
      this.initializeJoditEditor();
    }
  }

  ngOnDestroy() {
    if (this.editorInstance) {
      this.editorInstance.destruct();
      this.editorInstance = null;
    }
    this.cleanupPreviewEditor();
  }

  /**
   * Implementazione del guard CanDeactivate
   * Previene la navigazione se ci sono modifiche non salvate
   * Blocca solo quando si sta modificando un template esistente (con templateId)
   */
  canDeactivate(): boolean | Promise<boolean> {
    // Se non ci sono modifiche, permetti sempre la navigazione
    if (!this.hasUnsavedTemplateChanges) {
      return true;
    }

    // Se stai creando un nuovo template (nessun templateId), permetti navigazione
    // L'utente può abbandonare la creazione senza conferma
    if (this.templateId === null) {
      return true;
    }

    // Se stai modificando un template esistente, chiedi conferma
    return new Promise<boolean>((resolve) => {
      this.confirmDialogService.confirmCancelEdit(
        () => resolve(true),  // accept: permetti navigazione
        () => resolve(false)  // reject: blocca navigazione
      );
    });
  }

  /**
   * Avvisa l'utente se prova a chiudere il tab/finestra con modifiche non salvate
   */
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.hasUnsavedTemplateChanges) {
      $event.returnValue = true;
    }
  }

  /**
   * Inizializza l'editor Jodit con configurazione completa
   */
  private initializeJoditEditor() {
    this.editorInstance = Jodit.make(this.joditEditorElement.nativeElement, {
      height: 500,
      language: 'it',
      toolbarButtonSize: 'middle',
      toolbarAdaptive: true,
      
      // Toolbar completa
      buttons: [
        'source', '|',
        'bold', 'italic', 'underline', 'strikethrough', '|',
        'superscript', 'subscript', '|',
        'ul', 'ol', 'outdent', 'indent', '|',
        'font', 'fontsize', 'brush', 'paragraph', '|',
        'image', 'file', 'video', 'table', 'link', '|',
        'align', 'undo', 'redo', '|',
        'hr', 'eraser', 'copyformat', '|',
        'symbol', 'fullsize', 'print', 'preview', 'find', '|',
        'selectall', 'cut', 'copy', 'paste'
      ],

      // Lista completa di dimensioni font (pari e dispari) per ridurre i casi di mismatch
      controls: {
        fontsize: {
          list: {
            '8': '8',
            '9': '9',
            '10': '10',
            '11': '11',
            '12': '12',
            '13': '13',
            '14': '14',
            '15': '15',
            '16': '16',
            '17': '17',
            '18': '18',
            '19': '19',
            '20': '20',
            '22': '22',
            '24': '24',
            '26': '26',
            '28': '28',
            '30': '30',
            '36': '36',
            '48': '48',
            '72': '72'
          }
        }
      },
      
      // Configurazione Uploader per immagini
      uploader: {
        insertImageAsBase64URI: true,
        imagesExtensions: ['jpg', 'png', 'jpeg', 'gif', 'svg', 'webp'],
        withCredentials: false,
        format: 'json',
        method: 'POST'
      },
      
      // Configurazione tabelle avanzate
      table: {
        selectionCellStyle: 'border: 1px double #1e88e5 !important;',
        useExtraClassesOptions: true,
        allowCellResize: true,
        allowTableResize: true,
        useInlineStyles: true,
      },
      
      // Configurazione link
      link: {
        followOnDblClick: false,
        processVideoLink: true,
        processPastedLink: true,
        removeLinkAfterFormat: true,
        noFollowCheckbox: true,
        openInNewTabCheckbox: true,
      },
      
      // Configurazione paste
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      defaultActionOnPaste: 'insert_clear_html' as any,
      
      // Editor di codice sorgente — beautifyHTML disabilitato: caricherebbe
      // js-beautify da CDN esterno bloccato dalla CSP (script-src 'self').
      sourceEditor: 'area',
      beautifyHTML: false,
      
      // Configurazioni avanzate
      showCharsCounter: true,
      showWordsCounter: true,
      showXPathInStatusbar: false,
      showBranding: false,
      
      // Configurazioni di formattazione
      enter: 'p',
      
      // Placeholder
      placeholder: 'Inizia a scrivere qui...',
      
      // Disabilita la sanitizzazione per preservare gli span personalizzati
      cleanHTML: {
        allowTags: true,
        allowAttributes: true,
        removeEmptyElements: false,
        fillEmptyParagraph: false
      },
      
      // Permetti tutti gli attributi data-*
      allowDataAttributes: true,
      
      // Non rimuovere span vuoti o con attributi personalizzati
      disablePlugins: ['clean-html'] as any,
      
      // CSS inline per l'editor
      editorCssClass: 'jodit-contract-editor',
      
      // Inserisci CSS personalizzato per l'editor
      iframeStyle: `
        .editor-variable {
          display: inline-block !important;
          padding: 2px 8px !important;
          margin: 0 2px !important;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          color: white !important;
          font-weight: 600 !important;
          font-style: italic !important;
          border-radius: 4px !important;
          font-size: 0.9em !important;
          cursor: default !important;
          user-select: none !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
        }
        .editor-variable:hover {
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3) !important;
        }
      `
    } as any);

    // Sincronizza il contenuto e marca come modificato (solo se non stiamo caricando)
    this.editorInstance.events.on('change', () => {
      this.editContent.set(this.editorInstance.value || '');
      if (!this.isLoadingTemplate) {
        this.markAsModified();
      }

      // Re-applica contenteditable="false" e rimuove font-size inline bloccato
      const iframeBody = this.editorInstance?.editor as HTMLElement;
      if (iframeBody) {
        iframeBody.querySelectorAll<HTMLElement>('.editor-variable').forEach((el) => {
          if (el.getAttribute('contenteditable') !== 'false') {
            el.setAttribute('contenteditable', 'false');
          }
          el.style.removeProperty('font-size');

          const parent = el.parentElement;
          if (
            parent &&
            parent !== iframeBody &&
            parent.tagName === 'SPAN' &&
            !parent.className &&
            parent.style.fontSize &&
            !parent.style.fontWeight &&
            !parent.style.fontStyle &&
            !parent.style.color &&
            !parent.style.textDecoration &&
            parent.childNodes.length === 1
          ) {
            parent.style.removeProperty('font-size');
            if (parent.style.length === 0 && parent.attributes.length === 0) {
              parent.replaceWith(...Array.from(parent.childNodes));
            }
          }
        });
      }
    });

    // Intercetta Backspace/Delete quando il cursore è finito dentro uno span variabile
    setTimeout(() => {
      const iframeBody = this.editorInstance?.editor as HTMLElement;
      if (!iframeBody) return;
      iframeBody.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key !== 'Backspace' && e.key !== 'Delete') return;
        const sel = iframeBody.ownerDocument.getSelection();
        if (!sel) return;

        let node: Node | null = sel.anchorNode;
        while (node && node !== iframeBody) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            if (el.classList.contains('editor-variable')) {
              e.preventDefault();
              el.remove();
              return;
            }
          }
          node = node.parentNode;
        }

        if (sel.isCollapsed) {
          const range = sel.getRangeAt(0);
          let adjacent: Node | null = null;
          if (e.key === 'Backspace') {
            adjacent = range.startOffset > 0
              ? (range.startContainer.childNodes[range.startOffset - 1] ?? null)
              : range.startContainer.previousSibling;
          } else {
            adjacent = range.startOffset < range.startContainer.childNodes.length
              ? (range.startContainer.childNodes[range.startOffset] ?? null)
              : range.startContainer.nextSibling;
          }
          if (adjacent && adjacent.nodeType === Node.ELEMENT_NODE) {
            const el = adjacent as HTMLElement;
            if (el.classList.contains('editor-variable')) {
              e.preventDefault();
              el.remove();
            }
          }
        }
      }, true);
    }, 500);



    // Imposta contenuto iniziale se presente
    const content = this.editContent();
    if (content) {
      this.isLoadingTemplate = true;
      this.editorInstance.value = content;
      // Usa setTimeout per resettare il flag dopo che tutti gli eventi sono stati processati
      setTimeout(() => {
        this.isLoadingTemplate = false;
      }, 0);
    }

    // Setup autocomplete per variabili
    this.setupAutocomplete(this.editorInstance);
  }

  loadTemplateById(templateId: string, isClone: boolean = false): void {
    this.templateRegistryService.getTemplate(Number(templateId)).pipe(
      switchMap((response) => {
        if (!response?.status || !response.data) {
          throw new Error('Invalid template response');
        }
        
        // Type guard: getTemplate returns single Template, not array or paginated
        const templateData = response.data;
        if (Array.isArray(templateData) || 'content' in templateData) {
          throw new Error('Unexpected data format from getTemplate');
        }
        
        const template = templateData as Template;
        
        // Get section IDs and prepare to load full content
        const sectionIds = (template.sections || []).map((section: TemplateSectionResponse) => section.sectionId);
        
        // If no sections, return empty array
        if (sectionIds.length === 0) {
          return of({ template, loadedSections: [] });
        }
        
        // Load full content for each section
        const sectionRequests = sectionIds.map(sectionId => 
          this.sectionRegistryService.getSection(sectionId).pipe(
            catchError(error => {
              console.error(`Error loading section ${sectionId}:`, error);
              return of(null);
            })
          )
        );
        
        return forkJoin(sectionRequests).pipe(
          switchMap(sectionResponses => {
            const loadedSections: ContractSection[] = [];
            
            // Map sections maintaining the original order from template.sections
            template.sections?.forEach((templateSection: TemplateSectionResponse, index: number) => {
              const sectionResponse = sectionResponses[index];
              
              if (sectionResponse?.status && sectionResponse.data) {
                const sectionData = sectionResponse.data as Section;
                
                // Map variables ID to displayName
                if (templateSection.variables) {
                  templateSection.variables.forEach(v => {
                    this.variablesMap.set(v.id, v.displayName);
                  });
                }
                
                loadedSections.push({
                  id: templateSection.sectionId,
                  title: templateSection.sectionName || sectionData.name || '',
                  content: sectionData.contentText || '',
                  variables: templateSection.variables,
                  attachments: templateSection.attachments
                });
              }
            });
            
            return of({ template, loadedSections });
          })
        );
      })
    ).subscribe({
      next: ({ template, loadedSections }) => {
        this.selectedSections.set(loadedSections);
        
        // Enrich availableAttachments with attachments from loaded sections
        loadedSections.forEach(section => {
          if (section.attachments && section.attachments.length > 0) {
            const currentAvailable = this.availableAttachments();
            section.attachments.forEach(att => {
              if (att.id && !currentAvailable.find(a => a.id === att.id)) {
                this.availableAttachments.update(arr => [...arr, att as any]);
              }
            });
          }
        });
        
        if (isClone) {
          // Clone mode: prefix name with "Copia di" and don't set templateId
          this.templateName = `Copia di ${template.name}`;
          this.templateId = null;
        } else {
          // Edit mode: use original name and set templateId
          this.templateName = template.name;
          this.templateId = Number(templateId);
          this.originalTemplateName = template.name;
          this.originalSelectedSections = [...loadedSections];
        }
        
        // Load description, domainType and subDomainType
        this.templateDescription = template.description || '';
        this.domainType = template.domainType || 'SUPPLIERS';
        this.subDomainType = template.subDomainType || 'CONTRACTS';
        
        // Load optional attachments - extract IDs from optionalAttachments array
        const optionalIds = template.optionalAttachments?.map(att => att.id).filter(id => id !== undefined) || 
                           template.optionalAttachmentIds || [];
        this.optionalAttachmentIds.set(optionalIds as number[]);
        
        // Enrich availableAttachments with optionalAttachments from template
        if (template.optionalAttachments && template.optionalAttachments.length > 0) {
          const currentAvailable = this.availableAttachments();
          template.optionalAttachments.forEach(att => {
            if (att.id && !currentAvailable.find(a => a.id === att.id)) {
              this.availableAttachments.update(arr => [...arr, att as any]);
            }
          });
        }
        
        // Reset unsaved changes flag after loading is complete
        // (ngModelChange may have triggered during data binding)
        this.isLoadingTemplate = true;
        this.hasUnsavedTemplateChanges = false;
        // Usa setTimeout per resettare il flag dopo che tutti gli eventi sono stati processati
        setTimeout(() => {
          this.isLoadingTemplate = false;
        }, 100);
        
        this.updateBreadcrumb();
      },
      error: (error) => {
        console.error('Error loading template:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare il template',
          life: 5000
        });
      }
    });
  }

  loadAvailableSections() {
    this.sectionRegistryService.getSections().subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          let sectionsArray: Section[] = [];
          
          if (Array.isArray(response.data)) {
            sectionsArray = response.data;
          } else if ('content' in response.data) {
            sectionsArray = response.data.content;
          }
          
          // Convert Section to ContractSection format
          const contractSections: ContractSection[] = sectionsArray.map(section => {
            // Map variables if present
            const variables = (section as any).variables;
            if (variables) {
              variables.forEach((v: any) => {
                this.variablesMap.set(v.id, v.displayName);
              });
            }
            return {
              id: section.id || 0,
              title: section.name,
              content: section.contentText || '',
              variables: variables,
              attachments: section.attachments
            };
          });
          
          this.sections.set(contractSections);
          this.filteredSections.set(contractSections);
        }
      },
      error: (error) => {
        console.error('Error loading sections:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare le sezioni'
        });
        this.sections.set([]);
        this.filteredSections.set([]);
      }
    });
  }

  loadAvailableAttachments() {
    this.attachmentRegistryService.getAttachments().subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          let attachmentsArray: Attachment[] = [];
          
          if (Array.isArray(response.data)) {
            attachmentsArray = response.data;
          } else if ('content' in response.data) {
            attachmentsArray = response.data.content as Attachment[];
          }
          
          this.availableAttachments.set(attachmentsArray);
        }
      },
      error: (error) => {
        console.error('Error loading attachments:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare gli allegati'
        });
        this.availableAttachments.set([]);
      }
    });
  }

  addOptionalAttachment(attachmentId: number) {
    const currentIds = this.optionalAttachmentIds();
    if (!currentIds.includes(attachmentId)) {
      this.optionalAttachmentIds.set([...currentIds, attachmentId]);
      this.markAsModified();
    }
  }

  removeOptionalAttachment(attachmentId: number) {
    const currentIds = this.optionalAttachmentIds();
    this.optionalAttachmentIds.set(currentIds.filter(id => id !== attachmentId));
    this.markAsModified();
  }

  isAttachmentInOptional(attachmentId: number): boolean {
    return this.optionalAttachmentIds().includes(attachmentId);
  }

  showAddAttachmentDialog() {
    this.attachmentSearchQuery = '';
    this.addAttachmentDialogVisible.set(true);
  }

  closeAddAttachmentDialog() {
    this.addAttachmentDialogVisible.set(false);
    this.attachmentSearchQuery = '';
  }

  getFilteredAvailableAttachments(): Attachment[] {
    // Escludi allegati già ereditati dalle sezioni
    const sectionAttachmentIds = new Set(this.sectionAttachments().map(a => a.id));
    const availableAttachments = this.availableAttachments().filter(att => 
      !sectionAttachmentIds.has(att.id)
    );
    
    // Applica filtro di ricerca
    const query = this.attachmentSearchQuery.toLowerCase().trim();
    if (!query) {
      return availableAttachments;
    }
    return availableAttachments.filter(att => 
      att.title.toLowerCase().includes(query) ||
      (att.description && att.description.toLowerCase().includes(query))
    );
  }

  toggleAttachmentSelection(attachmentId: number) {
    if (this.isAttachmentInOptional(attachmentId)) {
      this.removeOptionalAttachment(attachmentId);
    } else {
      this.addOptionalAttachment(attachmentId);
    }
  }

  filterSections() {
    const query = this.searchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredSections.set(this.sections());
    } else {
      const filtered = this.sections().filter(s => 
        s.title.toLowerCase().includes(query) ||
        (s.content && s.content.toLowerCase().includes(query))
      );
      this.filteredSections.set(filtered);
    }
  }

  saveTemplate() {
    this.saveTemplateWithStatus('ACTIVE');
  }

  saveDraft() {
    this.saveTemplateWithStatus('DRAFT');
  }

  private saveTemplateWithStatus(status: 'DRAFT' | 'ACTIVE', callback?: () => void) {
    if (!this.templateName.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Inserisci un nome per il template',
        life: 3000
      });
      return;
    }
    
    if (this.selectedSections().length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Seleziona almeno una sezione',
        life: 3000
      });
      return;
    }
    
    // Create Template object
    const template: Template = {
      name: this.templateName,
      description: this.templateDescription.trim() || undefined,
      domainType: this.domainType,
      subDomainType: this.subDomainType,
      status: status,
      sections: this.selectedSections().map((section, index) => ({
        sectionId: section.id,
        sectionOrder: index + 1
      })),
      optionalAttachmentIds: this.optionalAttachmentIds().length > 0 ? this.optionalAttachmentIds() : undefined
    };

    const saveObs = this.templateId
      ? this.templateRegistryService.updateTemplatePut(this.templateId, template)
      : this.templateRegistryService.createTemplate(template);

    saveObs.subscribe({
      next: (response) => {
        if (response?.status) {
          const isDraft = status === 'DRAFT';
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: isDraft 
              ? (this.templateId ? 'Bozza aggiornata con successo' : 'Bozza salvata con successo')
              : (this.templateId ? 'Template aggiornato con successo' : 'Template salvato con successo'),
            life: 3000
          });
          
          this.hasUnsavedTemplateChanges = false;
          this.originalTemplateName = this.templateName;
          this.originalSelectedSections = [...this.selectedSections()];
          
          // Execute callback if provided
          if (callback) {
            callback();
          }
          
          // Navigate back to templates registry only if saving as ACTIVE template and no callback
          if (status === 'ACTIVE' && !callback) {
            this.router.navigate([this.templatesRegistryRoute]);
          }
        }
      },
      error: (error) => {
        console.error('Error saving template:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile salvare il template',
          life: 5000
        });
      }
    });
  }

  cancelTemplate() {
    // Naviga al registro template - il guard canDeactivate si occuperà di confermare se ci sono modifiche non salvate
    this.router.navigate([this.templatesRegistryRoute]);
  }

  resetTemplate() {
    this.templateName = '';
    this.templateDescription = '';
    this.selectedSections.set([]);
    this.originalTemplateName = '';
    this.originalSelectedSections = [];
    this.hasUnsavedTemplateChanges = false;
    this.domainType = this.getInitialDomainType(); // Determina in base al contesto
    this.subDomainType = 'CONTRACTS';
    this.searchQuery = '';
    this.filteredSections.set(this.sections());
    this.optionalAttachmentIds.set([]);
  }

  /**
   * Determina il domainType iniziale in base al contesto di navigazione
   * Fornitori (SUPPLIERS) o Fiduciari (PROFESSIONALS)
   */
  private getInitialDomainType(): string {
    return this.navigationContext.isSupplierContext() ? 'SUPPLIERS' : 'PROFESSIONALS';
  }

  insertVariable(varKey: string) {
    const variableTag = `<span class="editor-variable" contenteditable="false">{{${varKey}}}</span>`;
    if (this.editorInstance && typeof this.editorInstance.selection !== 'undefined') {
      // Jodit API: usa selection.insertHTML per inserire HTML
      this.editorInstance.selection.insertHTML(variableTag);
    }
  }

  setupAutocomplete(editor: any) {
    let autocompleteMenu: HTMLElement | null = null;
    let autocompleteActive = false;
    let filteredVariables: typeof this.variables = [];
    let selectedIndex = 0;

    const createAutocompleteMenu = (x: number, y: number, query: string = '') => {
      if (autocompleteMenu) {
        autocompleteMenu.remove();
        autocompleteMenu = null;
      }

      filteredVariables = this.variables.filter(v =>
        v.key.toLowerCase().includes(query.toLowerCase())
      );

      if (filteredVariables.length === 0) return;

      autocompleteMenu = document.createElement('div');
      autocompleteMenu.className = 'tinymce-autocomplete-menu';
      autocompleteMenu.style.cssText = `
        position: fixed;
        left: ${x}px;
        top: ${y}px;
        background: white;
        border: 1px solid #ccc;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        z-index: 10000;
        max-height: 300px;
        overflow-y: auto;
        min-width: 250px;
        pointer-events: none;
      `;

      filteredVariables.forEach((variable, index) => {
        const item = document.createElement('div');
        item.className = 'autocomplete-item';
        item.style.cssText = `
          padding: 8px 12px;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.2s;
          pointer-events: auto;
        `;
        item.textContent = `{{${variable.key}}}`;
        item.dataset['index'] = index.toString();

        item.addEventListener('mouseenter', () => {
          selectedIndex = index;
          updateSelection();
        });

        item.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          insertSelectedVariable();
        });

        autocompleteMenu!.appendChild(item);
      });

      document.body.appendChild(autocompleteMenu);
      autocompleteActive = true;
      selectedIndex = 0;
      updateSelection();
    };

    const updateSelection = () => {
      if (!autocompleteMenu) return;
      const items = autocompleteMenu.querySelectorAll('.autocomplete-item');
      items.forEach((item, index) => {
        const htmlItem = item as HTMLElement;
        htmlItem.style.background = index === selectedIndex ? '#e3f2fd' : '';
      });
    };

    const closeAutocomplete = () => {
      if (autocompleteMenu) {
        autocompleteMenu.remove();
        autocompleteMenu = null;
      }
      autocompleteActive = false;
    };

    const insertSelectedVariable = () => {
      const variable = filteredVariables[selectedIndex];
      if (variable) {
        const variableHtml = `<span class="editor-variable" contenteditable="false">{{${variable.key}}}</span>`;
        editor.selection.insertHTML(variableHtml);
        closeAutocomplete();
        editor.selection.focus();
      }
    };

    editor.events.on('input', (e: any) => {
      const sel = editor.selection.sel;
      if (!sel || sel.rangeCount === 0) {
        closeAutocomplete();
        return;
      }

      const rng = sel.getRangeAt(0);
      const node = rng.startContainer;
      
      if (node.nodeType !== 3) {
        closeAutocomplete();
        return;
      }
      
      const text = node.textContent || '';
      const cursorPos = rng.startOffset;
      const beforeText = text.substring(0, cursorPos);
      const markerIndex = beforeText.lastIndexOf('{{');
      
      if (markerIndex !== -1) {
        const query = beforeText.substring(markerIndex + 2);
        const editorElement = editor.container;
        if (editorElement) {
          const editorRect = editorElement.getBoundingClientRect();
          const rect = rng.getBoundingClientRect();
          if (rect) {
            createAutocompleteMenu(editorRect.left + rect.left, editorRect.top + rect.bottom, query);
          }
        }
      } else {
        closeAutocomplete();
      }
    });

    editor.events.on('keydown', (e: KeyboardEvent) => {
      if (!autocompleteActive) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectedIndex = (selectedIndex + 1) % filteredVariables.length;
        updateSelection();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectedIndex = (selectedIndex - 1 + filteredVariables.length) % filteredVariables.length;
        updateSelection();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        insertSelectedVariable();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeAutocomplete();
      }
    });

    editor.events.on('blur', () => {
      setTimeout(() => closeAutocomplete(), 200);
    });
  }

  addSection(section: ContractSection) {
    // Add section preserving original BE ID - duplicates are tracked by array index
    this.selectedSections.update((prev) => [...prev, { ...section }]);
    
    // Enrich availableAttachments with attachments from the added section
    if (section.attachments && section.attachments.length > 0) {
      const currentAvailable = this.availableAttachments();
      section.attachments.forEach(att => {
        if (att.id && !currentAvailable.find(a => a.id === att.id)) {
          this.availableAttachments.update(arr => [...arr, att as any]);
        }
      });
      
      // Rimuovi dagli allegati opzionali quelli ora ereditati dalla sezione appena aggiunta
      const sectionAttachmentIds = section.attachments.map(a => a.id).filter(id => id !== undefined);
      const currentOptional = this.optionalAttachmentIds();
      const updatedOptional = currentOptional.filter(id => !sectionAttachmentIds.includes(id));
      if (updatedOptional.length !== currentOptional.length) {
        this.optionalAttachmentIds.set(updatedOptional);
      }
    }
    
    this.markAsModified();
  }

  removeSection(index: number) {
    this.selectedSections.update((prev) => prev.filter((_, i) => i !== index));
    this.markAsModified();
  }

  /**
   * Marca il template come modificato (modifiche non salvate)
   */
  markAsModified() {
    this.hasUnsavedTemplateChanges = true;
  }

  isSelected(id: number): boolean {
    return this.selectedSections().some((s) => s.id === id);
  }

  /**
   * Naviga al registro blocchi di testo per modificare la sezione nel suo editor originale
   * Controlla se ci sono modifiche non salvate e chiede conferma
   */
  editSection(section: ContractSection, index: number) {
    // Controlla se ci sono modifiche non salvate
    if (this.hasUnsavedTemplateChanges) {
      this.confirmDialogService.confirmCancelEdit(
        () => {
          // L'utente ha confermato di voler procedere perdendo le modifiche
          // Resetta il flag per evitare che il guard mostri un secondo dialog
          this.hasUnsavedTemplateChanges = false;
          this.navigateToSection(section.id);
        },
        () => {
          // L'utente annulla, rimane nella pagina per salvare manualmente se vuole
        }
      );
    } else {
      // Nessuna modifica non salvata, naviga direttamente
      this.navigateToSection(section.id);
    }
  }

  /**
   * Naviga al componente blocchi di testo per modificare una sezione specifica
   */
  private navigateToSection(sectionId: number) {
    this.router.navigate([this.sectionsRegistryRoute], {
      queryParams: { editSectionId: sectionId }
    });
  }

  saveEdit() {
    const index = this.editingSectionIndex();
    if (index !== null && this.editorInstance) {
      const content = this.editorInstance.value;
      this.selectedSections.update((prev) =>
        prev.map((s, i) => (i === index ? { ...s, content } : s))
      );
    }
    this.cancelEdit();
  }

  cancelEdit() {
    if (this.editorInstance) {
      this.editorInstance.remove();
      this.editorInstance = null;
    }
    this.editVisible.set(false);
    this.editingSectionIndex.set(null);
    this.editContent.set('');
  }

  openPreview() {
    // Costruisce il contenuto completo
    let fullContent = '';
    this.selectedSections().forEach((section) => {
      fullContent += section.content;
    });
    
    if (!fullContent) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Nessuna sezione selezionata da visualizzare in anteprima'
      });
      return;
    }
    
    // Format variables for preview display
    const formattedContent = this.formatVariablesForPreview(fullContent);
    
    this.previewContent.set(formattedContent);
    this.previewVisible.set(true);
  }

  previewSection(section: ContractSection) {
    if (!section.content) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Questa sezione non ha contenuto da visualizzare'
      });
      return;
    }
    
    // Format variables for preview display
    const formattedContent = this.formatVariablesForPreview(section.content);
    
    this.previewContent.set(formattedContent);
    this.previewVisible.set(true);
  }

  /**
   * Format variables for preview display
   * Converts {{varKey.id}} to styled HTML spans with variable displayName as display text
   * Uses the ID to lookup the displayName from the variablesMap
   */
  private formatVariablesForPreview(content: string): string {
    // Pattern matches: {{varKey.id}} - captures both varKey and id
    const variablePattern = /\{\{([^.}]+)\.(\d+)\}\}/g;
    
    return content.replace(variablePattern, (match, varKey, varId) => {
      const id = parseInt(varId, 10);
      // Try to get displayName from variablesMap using the ID
      let displayText = this.variablesMap.get(id);
      
      // Fallback: if not found in map, try to find by key in the global variables list
      if (!displayText) {
        const variable = this.variables.find(v => v.key === varKey);
        displayText = variable ? variable.displayName : varKey;
      }
      
      // Using solid background-color instead of gradient for Word export compatibility
      return `<span class="editor-variable" style="display: inline-block; padding: 2px 8px; margin: 0 2px; background-color: #6c63ff; color: white; font-weight: 600; font-style: italic; border-radius: 4px; font-size: 0.9em;">${displayText}</span>`;
    });
  }

  closePreview() {
    this.previewVisible.set(false);
    this.previewContent.set('');
  }

  cleanupPreviewEditor() {
    if (this.previewEditorInstance) {
      try {
        this.previewEditorInstance.remove();
      } catch (e) {
        console.error('Error removing preview editor:', e);
      }
      this.previewEditorInstance = null;
    }
    
    // Rimuovi eventuali textarea temporanei rimasti
    const tempElements = ['tinymce-preview-temp', 'tinymce-preview-section-temp'];
    tempElements.forEach(id => {
      const elem = document.getElementById(id);
      if (elem) {
        elem.remove();
      }
    });
  }

  private getFullContent(): string {
    let fullContent = '';
    this.selectedSections().forEach((section) => {
      fullContent += section.content;
    });
    return fullContent;
  }

  print() {
    window.print();
  }

  generateWord() {
    const content = this.previewContent() || this.getFullContent();
    
    if (!content) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Nessun contenuto da esportare'
      });
      return;
    }

    this.isGenerating.set(true);

    // Small delay to show loading indicator
    setTimeout(() => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      
      const emptyCells = tempDiv.querySelectorAll('td:empty, th:empty');
      emptyCells.forEach(cell => {
        cell.innerHTML = '&nbsp;';
      });
      
      const allCells = tempDiv.querySelectorAll('td, th');
      allCells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        htmlCell.style.minHeight = '20px';
        htmlCell.style.minWidth = '50px';
        htmlCell.style.padding = '5px';
        
        const borderStyle = htmlCell.style.borderStyle;
        if (borderStyle === 'hidden' || borderStyle === 'none') {
          htmlCell.style.border = 'none';
          htmlCell.style.borderStyle = 'none';
        } else {
          // ✅ FIX: Jodit non salva border-width e border-style, ma solo border-color
          // Se la cella ha border-color ma manca border-width o border-style, aggiungiamoli
          const hasBorderColor = htmlCell.style.borderColor && htmlCell.style.borderColor !== '';
          const hasBorderWidth = htmlCell.style.borderWidth && htmlCell.style.borderWidth !== '';
          const hasBorderStyle = htmlCell.style.borderStyle && htmlCell.style.borderStyle !== '';
          
          if (hasBorderColor && (!hasBorderWidth || !hasBorderStyle)) {
            if (!hasBorderWidth) {
              htmlCell.style.borderWidth = '1px';
            }
            if (!hasBorderStyle) {
              htmlCell.style.borderStyle = 'solid';
            }
          }
        }
      });
      
      // ✅ FIX: Assicura border-collapse sulle tabelle
      const allTables = tempDiv.querySelectorAll('table');
      allTables.forEach((table: Element) => {
        const htmlTable = table as HTMLElement;
        if (!htmlTable.style.borderCollapse) {
          htmlTable.style.borderCollapse = 'collapse';
        }
      });
      
      const cleanHtml = tempDiv.innerHTML;

      try {
        // Wrap HTML with proper UTF-8 encoding declaration
        const wrappedHtml = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
          </head>
          <body>
            ${cleanHtml}
          </body>
          </html>
        `;
        
        const converted = (window as any).htmlDocx.asBlob(wrappedHtml);
        const url = window.URL.createObjectURL(converted);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'contract_template.docx';
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error generating Word:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante la generazione del documento Word'
        });
      } finally {
        this.isGenerating.set(false);
      }
    }, 100);
  }

  generatePdf() {
    const content = this.previewContent() || this.getFullContent();
    
    if (!content) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Nessun contenuto da esportare'
      });
      return;
    }

    this.isGenerating.set(true);

    // Small delay to show loading indicator
    setTimeout(() => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      tempDiv.style.padding = '20px';
      tempDiv.style.fontFamily = 'Arial, sans-serif';
      
      const allTables = tempDiv.querySelectorAll('table');
      allTables.forEach((table: Element) => {
        const htmlTable = table as HTMLElement;
        htmlTable.style.borderCollapse = 'collapse';
        htmlTable.style.width = '100%';
        htmlTable.style.marginBottom = '10px';
      });
      
      const allCells = tempDiv.querySelectorAll('td, th');
      allCells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        if (!htmlCell.innerHTML.trim()) {
          htmlCell.innerHTML = '&nbsp;';
        }
        htmlCell.style.padding = '8px';
        htmlCell.style.minHeight = '20px';
      });
      
      const cleanHtml = tempDiv.innerHTML;

      const opt = {
        margin: 10,
        filename: 'contract_template.pdf',
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm' as const, format: 'a4' as const, orientation: 'portrait' as const }
      };

      try {
        html2pdf().set(opt).from(cleanHtml).save().then(() => {
          this.isGenerating.set(false);
        }).catch((error: any) => {
          console.error('Error generating PDF:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore durante la generazione del PDF'
          });
          this.isGenerating.set(false);
        });
      } catch (error) {
        console.error('Error generating PDF:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante la generazione del PDF'
        });
        this.isGenerating.set(false);
      }
    }, 100);
  }
}
